// Local-network access requests (accounts, docs/backend/accounts.md "Threedom-set fields"): a game asks to reach
// exact local origins (a companion app on the player's machine, a LAN server). Moderators review the queue here;
// a game's creator sees their own pending request on the game's dashboard page.

import { api, ApiError } from "../api.ts";
import { gameById, type Catalogue, type Game } from "../catalogue.ts";
import { clear, fmtDate, h, link, section, toast } from "../dom.ts";
import { nameSpan } from "./game.ts";

export interface LocalNetworkRequest {
  id: string;
  gameId: string;
  origins: string[];
  status: "pending" | "approved" | "rejected" | "superseded";
  filedBy: string;
  via: "user" | "deploy";
  note: string | null;
  decidedBy: string | null;
  decidedAt: number | null;
  createdAt: number;
}

/** Each requested origin, marked when it isn't in the game's current list (the part that widens access). */
export function originDiff(requested: string[], current: string[]): { origin: string; added: boolean }[] {
  const now = new Set(current);
  return requested.map((origin) => ({ origin, added: !now.has(origin) }));
}

/** Origins the request would drop. */
export function removedOrigins(requested: string[], current: string[]): string[] {
  const next = new Set(requested);
  return current.filter((o) => !next.has(o));
}

const currentOf = (g: Game | undefined): string[] => g?.localNetwork ?? [];

function originList(requested: string[], current: string[]): HTMLElement {
  const removed = removedOrigins(requested, current);
  return h(
    "ul",
    { class: "origins" },
    originDiff(requested, current).map((o) => h("li", { class: o.added ? "added" : "" }, h("code", null, o.origin), o.added ? h("span", { class: "badge warn" }, "New") : null)),
    removed.map((o) => h("li", { class: "removed" }, h("code", null, o), h("span", { class: "badge" }, "Dropped"))),
    requested.length === 0 && removed.length === 0 ? h("li", { class: "dim" }, "No origins") : null,
  );
}

/** /dashboard/local-network: the moderators' queue. */
export function localNetworkQueue(cat: Catalogue): HTMLElement {
  const host = h("div");
  const load = () =>
    clear(
      host,
      section(null, async (body) => {
        let reqs: LocalNetworkRequest[];
        try {
          reqs = (await api<{ requests: LocalNetworkRequest[] }>("accounts", "/catalogue/local-network?status=pending&limit=100")).requests;
        } catch (e) {
          if (e instanceof ApiError && (e.code === "not_moderator" || e.status === 403)) return clear(body, h("p", null, "Only Banger moderators review local-network requests."));
          throw e;
        }
        if (!reqs.length) return clear(body, h("p", { class: "dim" }, "No requests waiting."));
        clear(
          body,
          h(
            "ul",
            { class: "reviews" },
            reqs.map((r) => {
              const g = gameById(cat, r.gameId);
              const note = h("input", { placeholder: "Note to the creator (optional)", maxlength: 500, "aria-label": "Note" }) as HTMLInputElement;
              const decide = async (what: "approve" | "reject") => {
                try {
                  await api("accounts", `/catalogue/local-network/${encodeURIComponent(r.id)}/${what}`, { body: note.value.trim() ? { note: note.value.trim() } : {} });
                  toast(what === "approve" ? "Approved: the catalogue now lists these origins" : "Rejected");
                  load();
                } catch (e) {
                  toast(e instanceof ApiError && e.code === "not_pending" ? "Someone already decided this one." : e instanceof Error ? e.message : String(e));
                  load();
                }
              };
              return h(
                "li",
                { class: "review" },
                h(
                  "div",
                  { class: "review-head" },
                  h("strong", null, g ? link(`/g/${g.id}`, null, g.title) : r.gameId),
                  g ? h("span", { class: "dim small" }, `by ${g.creator.name}`) : null,
                  h("span", { class: "dim small" }, `${r.via === "deploy" ? "from the Threedom CLI (thrdm deploy)" : "filed"} by `, nameSpan(r.filedBy), ` · ${fmtDate(r.createdAt)}`),
                ),
                originList(r.origins, currentOf(g)),
                r.note ? h("p", { class: "small" }, "Creator's note: ", r.note) : null,
                h("p", { class: "dim small" }, "Approving lets the game reach exactly these addresses on the player's machine or network. Anything marked New widens what it can reach today."),
                h("div", { class: "actions" }, note, h("button", { type: "button", class: "btn small", onclick: () => decide("approve") }, "Approve"), h("button", { type: "button", class: "btn small danger", onclick: () => decide("reject") }, "Reject")),
              );
            }),
          ),
        );
      }),
    );
  load();
  return h(
    "div",
    null,
    h("h1", null, "Local network requests"),
    h("p", { class: "dim" }, "Games can't reach the player's own machine or local network unless they ask for exact addresses and a moderator approves them."),
    host,
  );
}

/** On a game's dashboard page: what it may reach now, and its pending request (the claimant may list their game's). */
export function localNetworkBox(g: Game): HTMLElement {
  return section(
    "Local network",
    async (body) => {
      const current = currentOf(g);
      const reqs = await api<{ requests: LocalNetworkRequest[] }>("accounts", `/catalogue/local-network?status=pending&game=${encodeURIComponent(g.id)}`)
        .then((r) => r.requests)
        .catch(() => [] as LocalNetworkRequest[]);
      const pending = reqs[0] ?? null;
      clear(
        body,
        current.length
          ? [h("p", null, "Your game may reach these local addresses:"), h("ul", { class: "origins" }, current.map((o) => h("li", null, h("code", null, o))))]
          : h("p", { class: "dim" }, "Your game can't reach the player's machine or local network. Most games never need to."),
        pending
          ? h(
              "div",
              { class: "note" },
              h("p", null, h("span", { class: "badge warn" }, "In review"), ` Filed ${fmtDate(pending.createdAt)}${pending.via === "deploy" ? " by the Threedom CLI" : ""}. A moderator will look at it.`),
              originList(pending.origins, current),
            )
          : null,
        h("p", { class: "dim small" }, "To ask for access, list the exact origins in ", h("code", null, "thrdm.json"), " (", h("code", null, '"localNetwork": ["http://localhost:4455"]'), ") and deploy. Removing origins applies at once; adding them waits for review."),
      );
    },
    { class: "panel" },
  );
}
